/**
 * Helpers for downloading a file the API streams back as a blob.
 *
 * Two things make this fiddlier than it looks. First, an error response to a request made
 * with `responseType: "blob"` arrives as a Blob too, so the JSON error message has to be
 * read out of it rather than off `error.response.data`. Second, the KOT client screens
 * intercept anchor clicks to warn about unsaved changes, so a download anchor has to opt
 * out with `data-ignore-dirty` or the guard swallows it (see AddClient.js).
 */

/**
 * Pulls the server-chosen filename out of a Content-Disposition header.
 *
 * Every failure here falls back to the caller's filename rather than throwing: the file is
 * already downloaded by this point, and a header we cannot parse is no reason to lose it.
 */
export function getFilenameFromDisposition(disposition) {
  if (!disposition) return null;

  const utf8Match = disposition.match(/filename\*=UTF-8''([^;]+)/i);
  if (utf8Match?.[1]) {
    try {
      return decodeURIComponent(utf8Match[1]);
    } catch {
      // A stray "%" that isn't a valid escape makes decodeURIComponent throw.
      return null;
    }
  }

  // An unquoted filename ends at the next parameter, so `;` has to stop the match too -
  // otherwise a trailing `; creation-date="..."` is swallowed into the name.
  const asciiMatch = disposition.match(/filename="([^"]+)"|filename=([^;]+)/i);
  return (asciiMatch?.[1] || asciiMatch?.[2])?.trim() || null;
}

/**
 * Reads the message out of an error body that came back as a Blob because the request
 * asked for one. Falls back to the raw text, then to `fallback`, so this never throws.
 */
export async function getErrorMessageFromBlob(blob, fallback = "Failed to download the file.") {
  try {
    const text = await blob.text();
    if (!text) return fallback;

    try {
      const json = JSON.parse(text);
      return json.message || json.error || fallback;
    } catch {
      return text;
    }
  } catch {
    return fallback;
  }
}

/** Triggers a browser download for an already-built object URL. */
function clickDownloadAnchor(url, filename) {
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  // Keeps the KOT unsaved-changes guard from intercepting this click.
  anchor.dataset.ignoreDirty = "true";
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
}

/**
 * Saves an axios blob response to disk, preferring the filename the server sent.
 *
 * `expectedContentType` guards against the case where the request succeeded at the HTTP
 * level but the body is actually a JSON error - without it the user gets a downloaded
 * file full of error text. Throws with the server's message instead.
 *
 * A missing Content-Type fails the guard rather than skipping it: a gateway error page is
 * exactly the kind of response that arrives without one, and saving that to disk under an
 * .xlsx name is the outcome the guard exists to prevent.
 */
export async function downloadBlobResponse(response, fallbackFilename, expectedContentType) {
  const contentType = response.headers?.["content-type"];

  if (expectedContentType && !contentType?.includes(expectedContentType)) {
    throw new Error(await getErrorMessageFromBlob(response.data));
  }

  const blob = new Blob([response.data], contentType ? { type: contentType } : undefined);
  const url = window.URL.createObjectURL(blob);

  try {
    const filename = getFilenameFromDisposition(response.headers?.["content-disposition"]) || fallbackFilename;
    clickDownloadAnchor(url, filename);
  } finally {
    // Deferred by a tick: Firefox and older WebKit queue the download from a programmatic
    // click, so revoking in the same tick can leave the user with a 0-byte file.
    setTimeout(() => window.URL.revokeObjectURL(url), 0);
  }
}

export const XLSX_CONTENT_TYPE = "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";
